import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Container, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { observer } from "mobx-react";
import PageTitle from "../../../../../views/components/common/pages/PageTitle";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import PropertyTable from "../../components/property/PropertyTable";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { PropertyFilters } from "../../../../../models/Property/PropertyFilters";

const PropertyListing: React.FC = () => {
  const { propertyStore } = useStores();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialParams: PropertyFilters = {
    page: searchParams.get("page") ? parseInt(searchParams.get("page")!, 10) : 1,
    limit: searchParams.get("limit") ? parseInt(searchParams.get("limit")!, 10) : 30,
    search_text: searchParams.get("search_text") || undefined,
  };

  const [params, setParams] = useState<PropertyFilters>(initialParams);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search_text") || "");

  useEffect(() => {
    propertyStore
      .getProperties(params)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message || "Something went wrong! Please try again later."
        )
      );
  }, [params, propertyStore]);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    if (params.page != null) queryParams.set("page", String(params.page));
    if (params.limit != null) queryParams.set("limit", String(params.limit));
    if (params.search_text) queryParams.set("search_text", params.search_text);
    const queryString = queryParams.toString();
    if (queryString) {
      navigate({ search: queryString }, { replace: true });
    }
  }, [params, navigate]);

  const handleSearchBar = (value: string) => {
    setSearchTerm(value ?? "");
    if (!value) {
      setParams((prev) => ({ ...prev, search_text: undefined, page: 1 }));
    }
  };

  const handleSearchSubmit = () => {
    setParams((prev) => ({
      ...prev,
      search_text: searchTerm.trim() || undefined,
      page: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      setParams((prev) => ({
        ...prev,
        search_text: searchTerm.trim() || undefined,
        page: 1,
      }));
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  return (
    <Box sx={{ paddingTop: 2 , backgroundColor: "white"}}>
      <Box
        sx={{
          marginTop: "0.6rem",
          pt: 5,
          pl: 4,
          pr: 4,
          pb: 1,
          boxShadow:
            "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
          borderRadius: "10px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            paddingBottom: 1,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle title="Properties" />
          <Link to="/property/create" style={{ textDecoration: "none" }}>
            <AppButton label="Add Property" startIcon={<AddIcon />} onClick={() => {}} />
          </Link>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            paddingBottom: 3,
            justifyContent: "space-between",
            alignContent: "center",
          }}
        >
          <CustomTextField
            placeholder="Search by name or ID"
            value={searchTerm}
            onSearch={(e) => handleSearchBar(e)}
            onKeyDown={(e) => handleKeyDown(e)}
            endAdornment={
              <SearchIcon
                onClick={() => handleSearchSubmit()}
                sx={{ color: "#7C7A7F", fontSize: "20px", cursor: "pointer" }}
              />
            }
            customStyles={{ width: "90%" }}
          />
        </Box>

        {propertyStore.loading && (
          <Box
            sx={{
              minHeight: "60vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!propertyStore.loading && propertyStore.properties.length === 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="10rem"
            textAlign="center"
          >
            <Typography variant="h6" fontWeight="500" color="text.secondary">
              {searchTerm ? `No results found for "${searchTerm}"` : "No properties found"}
            </Typography>
          </Box>
        )}

        {!propertyStore.loading && propertyStore.properties.length > 0 && (
          <PropertyTable
            properties={propertyStore.properties}
            page={params.page ?? 1}
            limit={params.limit ?? 30}
            total_count={propertyStore.total_count}
            total_pages={propertyStore.total_pages}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )}
      </Box>
    </Box>
  );
};

export default observer(PropertyListing);
